import { useApolloClient } from '@apollo/client';
import { useRouter } from 'next/router';
import React, { ComponentType, useEffect, useState } from 'react';
import { ErrorBoundary, FallbackProps as REBFallbackProps } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';
import Spinner from '../components/designsystem/Spinner';
import ErrorFallback from '../components/ErrorFallback';
import { useTenantContext } from '../context/TenantContext';
import { logout, refresh } from '../utils/auth';
import getApolloErrorType from '../utils/getApolloErrorType';
import sentry from '../utils/sentry';
import { useAuth, UseAuthOptions } from '../hooks/useAuth';

const { captureException } = sentry();

interface FallbackProps extends REBFallbackProps {
    onError: (error: Error, setErrorHandled: (handled: boolean) => void) => void;
}

const Fallback = (props: FallbackProps) => {
    const [errorHandled, setErrorHandled] = useState(true);
    const { brandConfig } = useTenantContext();
    useEffect(() => {
        if (props.error) {
            props.onError(props.error, setErrorHandled);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (errorHandled) {
        return (
            <Spinner
                message={<FormattedMessage defaultMessage="Mijn {island}" values={{ island: brandConfig.name }} />}
            />
        );
    }

    return <ErrorFallback {...props} />;
};

export interface WithAuthProps {
    loading: boolean;
    isAuthorized: boolean;
}

const withAuth = <T extends object>(
    Component: ComponentType<React.PropsWithChildren<T>>,
    options: UseAuthOptions & {
        Spinner?: ComponentType<React.PropsWithChildren<unknown>>;
        /**
         * In order to handle its own loading we can use a loading prop passed to the child and show a spinner there
         */
        withLoadingProp?: boolean;
    } = {}
) => {
    function Enhanced(props: T) {
        const { Spinner: SpinnerFromOptions, withLoadingProp, ...useAuthOptions } = options;
        const SpinnerComponent = SpinnerFromOptions ?? Spinner;

        const { failUrl, showSpinner, isAuthorized } = useAuth(useAuthOptions);

        const router = useRouter();
        const client = useApolloClient();
        const { brandConfig } = useTenantContext();

        if (!withLoadingProp && showSpinner) {
            return (
                <SpinnerComponent
                    message={<FormattedMessage defaultMessage="Mijn {island}" values={{ island: brandConfig.name }} />}
                />
            );
        }

        return (
            <ErrorBoundary
                fallbackRender={errProps => (
                    <Fallback
                        onError={async (error, setErrorHandled) => {
                            captureException(error);
                            const errorType = getApolloErrorType(error) ?? '500';
                            if (['401'].includes(errorType)) {
                                const success = await refresh();
                                if (!success) {
                                    logout();
                                    await client.resetStore();
                                    if (failUrl) {
                                        router.replace(failUrl);
                                        return;
                                    }
                                }
                                errProps.resetErrorBoundary();
                            } else {
                                setErrorHandled(false);
                            }
                        }}
                        {...errProps}
                    />
                )}
            >
                <Component {...props} loading={showSpinner} isAuthorized={isAuthorized} />
            </ErrorBoundary>
        );
    }

    Enhanced.displayName = `WithAuthComponent`;

    return Enhanced as ComponentType<React.PropsWithChildren<Omit<T, keyof WithAuthProps>>>;
};

export default withAuth;
